/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   input masking functions, based off of the one in web-pro
 */

import { memoizeResultsFunc } from '@zcode/memoizers'

const matchOperatorsRegex = /[|\\{}()[\]^$+*?.-]/g

export const escapeStringRegExp = (str: string) => {
  return str.replace(matchOperatorsRegex, '\\$&')
}

// NOTE: format chars should always be single chars, although TS does not enforce
export interface FormatChars {
  [index: string]: string
}

const DEFAULT_FORMAT_CHARS = {
  '*': '[A-Za-z0-9]',
  '9': '[0-9]',
  A: '[A-Z]',
  a: '[A-Za-z]',
}

const convertFormatChars = memoizeResultsFunc(
  (mask: string, formatChars: FormatChars) => {
    const re = new RegExp(`[^${Object.keys(formatChars).join('')}]`, 'g')
    let converted = mask
      .replace(re, '')
      .split('')
      .map((char) => `(?:.*?(${formatChars[char]})`)
      .join('')

    // can be undefined or 0
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const parenCount = converted.match(/\(\?:/g)?.length || 0
    converted += '?)'.repeat(parenCount)
    return converted
  }
)

const selectFormatCharRegExp = memoizeResultsFunc(
  (formatChars: FormatChars) => {
    return new RegExp(
      Object.keys(formatChars).map(escapeStringRegExp).join('|'),
      'g'
    )
  }
)

const selectMaskRegExp = memoizeResultsFunc(
  (mask: string, formatChars: FormatChars) => {
    return new RegExp(convertFormatChars(mask, formatChars))
  }
)

export const selectMaskedValue = memoizeResultsFunc(
  (
    inputValue: string | undefined,
    mask: string,
    formatChars: FormatChars = DEFAULT_FORMAT_CHARS,
    selectionStart: number | null = null
  ): { newValue: string; selectionStart: number | null } => {
    let newValue = ''
    let value = inputValue

    if (/\|U$/.test(mask)) {
      mask = mask.substring(0, mask.length - 2)
      value = inputValue?.toUpperCase()
    }

    if (!value) {
      return { newValue, selectionStart }
    }

    const re = selectMaskRegExp(mask, formatChars)
    const matches = value.match(re)

    if (matches?.[1]) {
      const chars = matches.slice(1).filter((v) => v != null)

      const formatCharRE = selectFormatCharRegExp(formatChars)
      newValue = chars.reduce(
        (v, char) => (char ? v.replace(/@@m/, char) : v),
        mask.replace(formatCharRE, '@@m')
      )

      if (newValue.includes('@@m')) {
        const firstPlaceholderIdx = newValue.indexOf('@@m')
        newValue = newValue.slice(0, firstPlaceholderIdx)
      }

      if (chars.length < matches.length - 1) {
        for (
          let index = newValue.length - 1;
          index && newValue[index] !== chars[chars.length - 1];
          index--
        ) {
          newValue = newValue.substring(0, newValue.length - 1)
        }
      }
    }

    // Adjust selectionStart to account for mask transformations
    let adjustedSelectionStart = selectionStart
    if (selectionStart !== null) {
      adjustedSelectionStart = selectMaskedValue(
        inputValue?.substring(0, selectionStart),
        mask,
        formatChars
      ).newValue.length
    }

    // Don't block users from typing the static chars in the format string
    const nextChar = mask.charAt(newValue.length)
    if (
      nextChar &&
      value.charAt(newValue.length) === nextChar &&
      !Object.keys(formatChars).includes(nextChar)
    ) {
      newValue += nextChar
    }

    return { newValue, selectionStart: adjustedSelectionStart }
  }
)
